import { useState, MouseEvent } from 'react';
import { Loader2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';

interface CreateShellUrlsProps {
  isCreating: boolean;
  setIsCreating: React.Dispatch<React.SetStateAction<boolean>>;
  onShellCreated?: () => void;
}

const CreateShellUrls: React.FC<CreateShellUrlsProps> = ({ isCreating, setIsCreating, onShellCreated }) => {
  const [urls, setUrls] = useState('');
  const [team, setTeam] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [hackerName, setHackerName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleUrlChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setUrls(event.target.value);
  };

  const handleTeamChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTeam(event.target.value);
  };

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value);
  };

  const handleUsernameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUsername(event.target.value);
  };

  const handleHackerNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setHackerName(event.target.value);
  };

  const splitUrls = () => {
    return urls.split('\n')?.filter(url => url.trim() !== '');
  };

  const resetForm = () => {
    setUrls('');
    setTeam('');
    setPassword('');
    setUsername('');
    setHackerName('');
    setError('');
  };

  const handleClose = () => {
    if (!isLoading) {
      resetForm();
      setIsCreating(false);
    }
  };

  const handleFormClick = (e: MouseEvent) => {
    e.stopPropagation();
  };

  const createShellUrls = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');

    try {
      const linksArray = splitUrls();

      if (!linksArray || linksArray.length === 0) {
        throw new Error('Please input at least one URL');
      }

      if (!team.trim()) {
        throw new Error('Team name is required');
      }

      if (!password.trim()) {
        throw new Error('Password is required');
      }

      setIsLoading(true);

      const response = await fetch(`/api/v1/link/`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          urls: linksArray,
          team: team,
          password: password,
          username: username.trim() || undefined,
          hacker_name: hackerName.trim() || undefined,
        }),
      });

      const res = await response.json();

      if (!response.ok) throw new Error(res.message || 'Failed to create links');

      toast.success('URLs successfully created');
      resetForm();
      setIsCreating(false);

      if (onShellCreated) {
        onShellCreated();
      }
    } catch (error: any) {
      setError(error.message || 'An error occurred while saving URLs. Please check your input.');
      toast.error(error.message || 'Failed to create URLs');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AlertDialog open={isCreating} onOpenChange={handleClose}>
      <AlertDialogContent className='sm:max-w-[600px] max-h-[90vh] overflow-y-auto'>
        <AlertDialogHeader className='mb-4'>
          <AlertDialogTitle className='text-xl font-bold'>Add New Shells</AlertDialogTitle>
          <AlertDialogDescription>Enter shell URLs and details below.</AlertDialogDescription>
        </AlertDialogHeader>

        <form onSubmit={createShellUrls} className='space-y-5 py-2' onClick={handleFormClick}>
          {error && (
            <Alert variant='destructive'>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className='grid gap-6'>
            <div className='space-y-2'>
              <Label htmlFor='team' className='text-sm font-medium'>
                Team Name
              </Label>
              <Input
                id='team'
                value={team}
                onChange={handleTeamChange}
                placeholder='Enter team name'
                disabled={isLoading}
                required
                className='focus:ring-2 focus:ring-offset-1'
              />
            </div>

            <div className='space-y-2'>
              <Label htmlFor='password' className='text-sm font-medium'>
                Password
              </Label>
              <Input
                id='password'
                value={password}
                onChange={handlePasswordChange}
                placeholder='Enter password'
                disabled={isLoading}
                required
                className='focus:ring-2 focus:ring-offset-1'
              />
            </div>

            <div className='space-y-2'>
              <Label htmlFor='username' className='text-sm font-medium'>
                Username
              </Label>
              <Input
                id='username'
                value={username}
                onChange={handleUsernameChange}
                placeholder='Enter username (optional)'
                disabled={isLoading}
                className='focus:ring-2 focus:ring-offset-1'
              />
            </div>

            <div className='space-y-2'>
              <Label htmlFor='hackerName' className='text-sm font-medium'>
                Hacker Name
              </Label>
              <Input
                id='hackerName'
                value={hackerName}
                onChange={handleHackerNameChange}
                placeholder='Enter hacker name'
                disabled={isLoading}
                required
                className='focus:ring-2 focus:ring-offset-1'
              />
            </div>

            <div className='space-y-2'>
              <Label htmlFor='urls' className='text-sm font-medium'>
                Shell URLs
              </Label>
              <Textarea
                id='urls'
                value={urls}
                onChange={handleUrlChange}
                placeholder='Enter shell URLs (one per line)'
                className='min-h-[180px] font-mono focus:ring-2 focus:ring-offset-1'
                disabled={isLoading}
                required
                onClick={e => e.stopPropagation()}
              />
              <p className='text-xs text-muted-foreground mt-1'>
                Enter one URL per line. They will be processed as separate shells.
              </p>
            </div>
          </div>

          <AlertDialogFooter className='mt-6 gap-2'>
            <AlertDialogCancel type='button' disabled={isLoading} onClick={e => e.stopPropagation()}>
              Cancel
            </AlertDialogCancel>
            <Button type='submit' disabled={isLoading} className='min-w-[120px]'>
              {isLoading ? (
                <>
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                  Saving...
                </>
              ) : (
                'Save Shells'
              )}
            </Button>
          </AlertDialogFooter>
        </form>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default CreateShellUrls;
